import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatMoney, formatNumber } from "../lib/api";
import { Plus, Trash2, FileSpreadsheet, Loader2 } from "lucide-react";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";

export default function StudiesList() {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const sort = useSortable(studies, "created_at", "desc");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studies");
      setStudies(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este estudio? Esta acción no se puede deshacer.")) return;
    await api.delete(`/studies/${id}`);
    load();
  };

  return (
    <div className="p-8 fade-in" data-testid="studies-list-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Listado</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1">Mis estudios</h1>
          <p className="text-slate-600 mt-1">{studies.length} {studies.length === 1 ? "estudio" : "estudios"}</p>
        </div>
        <Link to="/upload" className="btn btn-primary" data-testid="new-study-button">
          <Plus size={16} /> Nuevo estudio
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Cargando…</div>
      ) : studies.length === 0 ? (
        <div className="card p-12 text-center">
          <FileSpreadsheet size={36} className="mx-auto text-slate-400" />
          <h3 className="font-display text-xl font-bold mt-3">No tienes estudios aún</h3>
          <p className="text-slate-600 mt-1 mb-4">Sube tu primer Excel para empezar a analizar.</p>
          <Link to="/upload" className="btn btn-primary" data-testid="empty-upload-button"><Plus size={16} /> Subir estudio</Link>
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-3">
            <ExportButtons
              filename="mis-estudios"
              title="Mis estudios"
              subtitle={`${studies.length} estudios`}
              sheetName="Estudios"
              columns={[
                { header: "Nombre", key: "name" },
                { header: "Periodo (días)", key: "period_days", align: "right" },
                { header: "Productos", key: "total_products", align: "right" },
                { header: "Stock (€)", key: "total_stock_value", format: (v) => (v || 0).toFixed(2), align: "right" },
                { header: "Ventas (€)", key: "total_sales_value", format: (v) => (v || 0).toFixed(2), align: "right" },
                { header: "Fecha", key: "created_at", format: (v) => v ? new Date(v).toLocaleDateString("es-ES") : "" },
              ]}
              rows={sort.sortedRows}
            />
          </div>
        <div className="card overflow-x-auto">
          <table className="dense" data-testid="studies-table">
            <thead>
              <tr>
                <th><SortHeader label="Nombre" k="name" sort={sort} defaultDir="asc" /></th>
                <th className="num"><SortHeader label="Periodo" k="period_days" sort={sort} num /></th>
                <th className="num"><SortHeader label="Productos" k="total_products" sort={sort} num /></th>
                <th className="num"><SortHeader label="Valor stock" k="total_stock_value" sort={sort} num /></th>
                <th className="num"><SortHeader label="Ventas" k="total_sales_value" sort={sort} num /></th>
                <th><SortHeader label="Fecha" k="created_at" sort={sort} /></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sort.sortedRows.map((s) => (
                <tr key={s.id} data-testid={`study-row-${s.id}`}>
                  <td>
                    <Link to={`/studies/${s.id}`} className="font-semibold hover:underline" data-testid={`study-link-${s.id}`}>
                      {s.name}
                    </Link>
                    {s.notes && <div className="text-xs text-slate-500 mt-0.5">{s.notes}</div>}
                  </td>
                  <td className="num font-mono">{s.period_days}d</td>
                  <td className="num">{formatNumber(s.total_products)}</td>
                  <td className="num">{formatMoney(s.total_stock_value)}</td>
                  <td className="num">{formatMoney(s.total_sales_value)}</td>
                  <td className="font-mono text-xs">{new Date(s.created_at).toLocaleDateString("es-ES")}</td>
                  <td className="text-right">
                    <button onClick={() => del(s.id)} className="text-red-600 hover:text-red-800 p-1" title="Eliminar" data-testid={`delete-study-${s.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
